import {assert} from 'Asserts';
import {
  DataPolicyActorCollaborator,
  DataPolicyGroupCollaborator,
  DataPolicyCollaborator,
  Permissions,
  Owner,
  Collaborator,
  OwnerInfo,
  GroupCollaborator,
} from 'auth/PermissionConstants';
import {getCollaboratorMemberPolicyId} from 'auth/PermissionUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Entity, EntityType, isOrganization, isUser} from 'constants/UserConstants';
import {Organization} from 'orgs/OrganizationConstants';
import {isTeam} from 'orgs/OrganizationUtil';
import {MakeFieldsRequired} from 'util/Types';

/**
 * Keep in sync with benchling/search/saved_search/shared_search_data_policies.py
 */
export enum SavedSearchPermissionLevel {
  VIEW = 'VIEW',
  EDIT = 'EDIT',
  ADMIN = 'ADMIN',
}

/**
 * Return a copy of the collaborator with the data policy ID set appropriately.
 */
export function setDataPolicyId(
  entity: Entity | DataPolicyCollaborator,
  policies: {
    dataPolicyId?: string;
    adminDataPolicyId?: string;
  }
): DataPolicyCollaborator {
  if (isUser(entity as {type?: EntityType})) {
    return {...entity, dataPolicyId: policies.dataPolicyId} as DataPolicyActorCollaborator;
  }
  if (isTeam(entity.id) || isOrganization(entity as {type?: EntityType})) {
    return {
      ...entity,
      ...(policies.dataPolicyId && {memberDataPolicyId: policies.dataPolicyId}),
      ...(policies.adminDataPolicyId && {adminDataPolicyId: policies.adminDataPolicyId}),
    } as DataPolicyGroupCollaborator;
  }
  assert(false, 'Cannot set data policy ID on unsupported entity', {entity});
}

// Keep in sync with SavedSearch.user_permissions_dict()
export interface SavedSearchPermissions extends MakeFieldsRequired<Permissions, 'owner' | 'deletable'> {
  canAddRemoveViewers: boolean;
  canAddRemoveEditors: boolean;
  canAddRemoveAdmins: boolean;
}

export function getOwnerInfo(owner: Owner, collaborators: Array<Collaborator>): OwnerInfo {
  return {
    type: owner.type!,
    memberDataPolicyId:
      owner.type === ModelTypeConstants.ORGANIZATION
        ? getMemberDataPolicyId(owner as Organization, collaborators)
        : null,
  };
}

export function getMemberDataPolicyId(org: Organization, collaborators: Array<Collaborator>): string | null {
  const orgCollaborator = collaborators.find((c) => c.id === org.id);
  if (orgCollaborator) {
    return getCollaboratorMemberPolicyId(orgCollaborator as GroupCollaborator);
  }
  return null;
}
